import { AppMachineContext } from '@/components/Functional/hooks-core/useRaundLifeCycle'
import { isLanguageSwitchable } from '@/components/Functional/utils/campaignLanguage'
import { Typography } from '@/components/UI/Typography'
import { useMenuStore } from '@/store/useMenuStore'
import { ActionIcon } from '@mantine/core'
import { IconBell, IconChevronLeft, IconMenu2, IconWorld, IconX } from '@tabler/icons-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './Header.module.css'
import { LanguageDialog } from './LanguageDialog'
import { MenuDrawer } from './MenuDrawer'

export type HeaderProps = {
  /** ヘッダータイトル */
  title?: string
  /**
   * true の場合は左側に「戻る ＜」アイコンを表示する。
   * false（デフォルト）の場合は「ハンバーガー / X」を表示する。
   */
  showBack?: boolean
  /** 戻るボタンクリック（showBack=true のときのみ有効） */
  onBack?: () => void
  /**
   * ハンバーガーメニュー（および MenuDrawer）を表示するか。
   * showBack=false のときのみ有効。false にすると左側アイコンを非表示にする（決済完了等の終端画面用）。
   */
  showMenu?: boolean
  /**
   * true の場合、このページで言語ボタンを右側に表示する（ホーム・スポット一覧・特典一覧・履歴のみ。#3664）。
   * true でも、キャンペーンが言語切替に対応していない（対応言語 1 件、または isLocalize === false）場合は表示しない。
   */
  showLanguage?: boolean
  /** ベルマークを出すかどうかのフラグ */
  showNotification?: boolean
  /** 通知バッジに表示する件数（0 または未指定でバッジ非表示） */
  notificationCount?: number
  /** 未読通知の赤い点バッジを表示するかどうか（件数なし）。notificationCount > 0 の場合は件数バッジが優先される */
  hasUnreadBadge?: boolean
  /** ベルボタンクリック */
  onNotification?: () => void
  className?: string
}

export function Header({
  title = 'タイトル',
  showBack = false,
  onBack,
  showMenu = true,
  showLanguage = false,
  showNotification = true,
  notificationCount,
  hasUnreadBadge = false,
  onNotification,
  className,
}: HeaderProps) {
  const { isOpen, toggle } = useMenuStore()
  const { t } = useTranslation()
  const [isLanguageDialogOpen, setIsLanguageDialogOpen] = useState(false)

  const campaignData = AppMachineContext.useSelector((s) => s.context.campaignData)

  // MenuDrawer を持つヘッダーでメニューを開いているときだけ「メニュー」表示に切り替える。
  // useMenuStore はグローバルで、SPA 遷移後も isOpen が残ることがあるため（#3669）、
  // 戻るボタン付き等のドロワーを持たないヘッダーでは isOpen を無視する
  const hasMenu = !showBack && showMenu
  const isMenuOpen = hasMenu && isOpen

  const canShowLanguage = showLanguage && isLanguageSwitchable(campaignData)
  const shouldShowLanguage = !isMenuOpen && canShowLanguage
  const shouldShowNotification = !isMenuOpen && showNotification
  const displayTitle = isMenuOpen ? t('common.menu', 'メニュー') : title

  return (
    <>
      <header className={`${styles.root} ${className ?? ''}`}>
        {/* 左側：showBack=true なら戻るアイコン、false ならハンバーガー/X */}
        <div className={styles.leftGroup}>
          {showBack ? (
            <ActionIcon variant="subtle" color="dark" onClick={onBack} aria-label={t('common.back', '戻る')}>
              <IconChevronLeft size={24} stroke={1.5} aria-hidden="true" />
            </ActionIcon>
          ) : showMenu ? (
            <ActionIcon
              variant="subtle"
              color="dark"
              onClick={toggle}
              aria-label={isOpen ? t('common.close_menu', 'メニューを閉じる') : t('common.open_menu', 'メニューを開く')}
            >
              {isOpen ? (
                <IconX size={24} stroke={1.5} aria-hidden="true" />
              ) : (
                <IconMenu2 size={24} stroke={1.5} aria-hidden="true" />
              )}
            </ActionIcon>
          ) : null}
        </div>

        <Typography variant="subtitle1" component="p" className={styles.title}>
          {displayTitle}
        </Typography>

        {/* 右側：言語ボタン（条件付き）＋ ベルボタン。メニュー展開中はどちらも出さない */}
        <div className={styles.rightGroup}>
          {shouldShowLanguage && (
            <ActionIcon
              variant="subtle"
              color="dark"
              onClick={() => setIsLanguageDialogOpen(true)}
              aria-label={t('common.language_settings', '言語設定')}
            >
              <IconWorld size={24} stroke={1.5} aria-hidden="true" />
            </ActionIcon>
          )}
          <div className={styles.notificationWrapper}>
            {shouldShowNotification && (
              <>
                <ActionIcon
                  variant="subtle"
                  color="dark"
                  onClick={onNotification}
                  aria-label={t('common.notifications', '通知')}
                >
                  <IconBell size={24} stroke={1.5} aria-hidden="true" />
                </ActionIcon>
                {notificationCount != null && notificationCount > 0 && (
                  <div className={styles.badge}>
                    <span className={styles.badgeText}>{notificationCount}</span>
                  </div>
                )}
                {hasUnreadBadge && !(notificationCount != null && notificationCount > 0) && (
                  <div className={styles.dotBadge} />
                )}
              </>
            )}
          </div>
        </div>
      </header>

      {/* ハンバーガー押下で表示されるドロワーメニュー（showBack=false かつ showMenu=true 時のみ） */}
      {hasMenu && <MenuDrawer />}

      {/* 言語ボタンを出せるときだけ描画する（メニュー開閉でアンマウントしないよう canShowLanguage で判定） */}
      {canShowLanguage && (
        <LanguageDialog
          isOpen={isLanguageDialogOpen}
          onClose={() => setIsLanguageDialogOpen(false)}
          languages={campaignData?.language ?? []}
        />
      )}
    </>
  )
}
